/**
 * Which permission this organisation currently holds over a customer, resolved from the list the
 * permission endpoint returns.
 *
 * **This file exists because the endpoint answers a broader question than the card asks.**
 * `GET /v1/permission?customerId=` returns every row for the customer, across every organisation,
 * in no order at all — `PermissionPersistenceService.java:82-87` is `find(...).list()` with no
 * `Sort`. Three steps stand between that and "may this organisation ask for a check": filter to
 * this organisation, order by recency, and read only the newest row's status.
 *
 * **All three are load-bearing, and skipping the middle one is a shipped production defect.**
 * VME-4158 filtered without ordering, hit an older revoked row, and reported a re-granted permission
 * as inactive. Revocation does not append a row — it updates the existing one to `Inactive`
 * (`PermissionPersistenceService.java:185-210`) — and a later re-grant does append, so a customer
 * who granted, revoked and granted again has two rows whose order is the whole answer.
 */

/**
 * The shape this resolver needs, and nothing more.
 *
 * Deliberately structural rather than the generated `Permission` type: every field on that type is
 * optional (`person-api.ts:1231` applies `.partial()` to the whole object), so a caller passing the
 * generated type satisfies this, and the resolver states exactly which four fields it reads.
 */
export type TPermissionRow = Readonly<{
  id?: string;
  organisationId?: string;
  permissionStatus?: string;
  addedDate?: string;
}>;

/**
 * What the card is allowed to conclude.
 *
 * Three values, not two, because "we did not find a permission" and "we could not tell" lead to
 * different words on screen: the first names a reason and a remedy, the second admits it has
 * neither. Collapsing them is how a page tells a client their customer withheld authorisation when
 * in fact a server was down.
 */
export type TRequestPermission = 'permitted' | 'notPermitted' | 'unknown';

/**
 * The status string that permits the request, compared exactly.
 *
 * **Only an explicit `Active` permits it.** Not "anything that is not `Inactive`": an unrecognised
 * or absent status is a value this code has never seen, and treating the unknown as permission is
 * the direction that offers an action the caller may not hold.
 */
const ACTIVE = 'Active';

/**
 * When a row was added, as an instant, or `undefined` if it cannot be established.
 *
 * **Parsed rather than compared as text.** `OffsetDateTime` is what the API declares, and two
 * legitimate values of it can carry different UTC offsets — `2025-06-01T00:00:00Z` and
 * `2025-06-01T10:00:00+10:00` are the same moment and sort differently as strings. Comparing the
 * text would pick the wrong current row for any customer whose rows were written from different
 * offsets, silently and only for them.
 *
 * An unparseable value is treated as no date at all rather than as a date at position zero.
 */
function addedAt(row: TPermissionRow): number | undefined {
  if (!row.addedDate) {
    return undefined;
  }

  const parsed = Date.parse(row.addedDate);

  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * Newest first.
 *
 * A row whose date cannot be established sorts **last**, never first: `addedDate` is optional on the
 * generated type, and a missing or malformed value at the front of the list would silently become
 * "the current permission".
 *
 * `id` breaks a tie so the result is stable. It is a UUID and carries no chronology, so it decides
 * nothing about recency — only that two runs over the same data agree. Rows equal on both keys
 * return `0`, as a comparator must: returning a preference for two indistinguishable elements is
 * inconsistent ordering, and engines are free to do anything with it.
 */
function newestFirst(a: TPermissionRow, b: TPermissionRow): number {
  const left = addedAt(a);
  const right = addedAt(b);

  if (left !== right) {
    if (left === undefined) {
      return 1;
    }

    if (right === undefined) {
      return -1;
    }

    return right - left;
  }

  const leftId = a.id ?? '';
  const rightId = b.id ?? '';

  if (leftId === rightId) {
    return 0;
  }

  return leftId < rightId ? 1 : -1;
}

/**
 * The rows this organisation holds over this customer, newest first.
 *
 * Exported for the tests that prove the ordering, and for nothing else — callers wanting an answer
 * want {@link requestPermissionFrom}.
 */
export function permissionsForOrganisation(
  permissions: readonly TPermissionRow[] | undefined,
  organisationId: string | undefined,
): TPermissionRow[] {
  if (!organisationId || !Array.isArray(permissions)) {
    return [];
  }

  // A row whose own `organisationId` is absent must not match an absent argument — the guard above
  // already refuses an absent argument, and this comparison is exact on both sides.
  return permissions.filter((row) => row?.organisationId === organisationId).sort(newestFirst);
}

/**
 * Whether this organisation may ask for a check, read from the permission list.
 *
 * - **No row for this organisation** → `notPermitted`. The customer has not authorised them; that is
 *   an answer, not an absence of one, and it is the state this ticket exists for.
 * - **One row** → its status decides. Ordering cannot change a single-element answer, so a missing
 *   `addedDate` is harmless here and is not treated as a defect.
 * - **Several rows, all dated** → the newest row's status decides.
 * - **Several rows, any of them undated or misdated** → `unknown`. With more than one candidate and
 *   no reliable recency, this code cannot say which row is current, and saying so is better than
 *   picking one. The caller renders that as "we cannot tell right now", never as "your customer
 *   refused".
 *
 *   **Reviewed for removal on 2026-08-29 and kept.** The case against it is R4: a guard whose
 *   trigger rate cannot be stated is a line in the tech-debt list, not code. What was checked, and
 *   what it settled: `PermissionEntity.addedDate` carries **no** `@Column(nullable = false)` —
 *   unlike `permissionStatus` immediately above it, which does — so a null is possible at the
 *   schema, and only one write path was found that sets it
 *   (`PermissionPersistenceService`'s create, `OffsetDateTime.now(...)`). That is not enough to
 *   remove it: finding one producer is not establishing there is only one, and migrations,
 *   backfills and direct SQL do not go through that path. Removing a guard on a permission decision
 *   needs evidence that the state cannot arise, and this is evidence that it is merely not known to
 *   — the opposite burden. It stays until someone can show the column is non-null in fact, at which
 *   point deleting it is three lines.
 *
 * **A single row is answered even without a date**, deliberately. Ordering cannot change a
 * one-element result, so the missing key costs nothing here, and reporting `unknown` for it would
 * withhold a control from a caller who holds the permission — the failure this ticket exists to
 * remove, arrived at from the other side. Design §11 A9 is narrowed to match: what a missing key
 * must never do is *sort to the front*, which is where it silently becomes "current".
 */
export function requestPermissionFrom(
  permissions: readonly TPermissionRow[] | undefined,
  organisationId: string | undefined,
): TRequestPermission {
  // No organisation, no answer. An empty result then means "nothing to compare against", not "no
  // permission", and the two must not collapse: the second is a statement about the customer.
  if (!organisationId) {
    return 'unknown';
  }

  const mine = permissionsForOrganisation(permissions, organisationId);

  if (mine.length === 0) {
    return 'notPermitted';
  }

  // With more than one candidate, every one of them has to be datable or there is no way to say
  // which is current. A row that cannot be dated is not evidence of anything, and picking one of the
  // remaining rows would be a guess wearing an answer's clothes.
  if (mine.length > 1 && mine.some((row) => addedAt(row) === undefined)) {
    return 'unknown';
  }

  return mine[0].permissionStatus === ACTIVE ? 'permitted' : 'notPermitted';
}
